/**
 * /customer-management — internal console over E2 customer headsets.
 *
 * The management side of the E2 Summit wall (/e2-summit): the same headline
 * numbers in the same order and the same leaderboard columns, plus what the
 * wall leaves out — per-headset history, who isn't recording, and email
 * controls. The wall links here with ?org=<org> to open a customer.
 *
 * Who is recording, who isn't, and the weekly accountability email that holds
 * each customer to 3 hours per headset per day. Reads /api/e2/console/overview
 * (the cached E2 snapshot + email settings); never scans Cosmos itself.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Alert from './Alert';
import Button from './Button';
import CustomerOrgDetail from './CustomerOrgDetail';
import DataTable, { DataTableColumn } from './DataTable';
import LoadingSpinner from './LoadingSpinner';
import {
  consoleApi, deviceSilent, E2Device, E2Org, EmailPreview, fmtH, fmtStamp, fmtWhen, Overview, pacificToday, silentLabel,
  Status, STATUS_LABEL, statusOf,
} from './customerManagement';
import './CustomerManagementPage.css';

type SortKey = 'week_hours' | 'pace' | 'headset_count' | 'silent' | 'org' | 'silent_headsets';

interface OrgRow extends E2Org {
  status: Status;
  silentHeadsets: number;
  neverHeadsets: number;
  rank: number;
}

const THRESHOLD_KEY = 'customerMgmt.silentDays';

function readThreshold(): number {
  try {
    const v = Number(localStorage.getItem(THRESHOLD_KEY));
    return v >= 1 && v <= 60 ? v : 3;
  } catch {
    return 3;
  }
}

const CustomerManagementPage: React.FC = () => {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [threshold, setThreshold] = useState<number>(readThreshold);
  const [sortKey, setSortKey] = useState<SortKey>('week_hours');
  const [sortDesc, setSortDesc] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | Status>('all');
  const [notRecordingOnly, setNotRecordingOnly] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // ?org= deep link from the wall opens that customer's drill-in
  const selectedOrg = searchParams.get('org');
  const setSelectedOrg = useCallback((org: string | null) => {
    setSearchParams(org ? { org } : {}, { replace: true });
  }, [setSearchParams]);
  const [confirmGlobalOn, setConfirmGlobalOn] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [teamText, setTeamText] = useState<string | null>(null);
  const [teamPreview, setTeamPreview] = useState<EmailPreview | null>(null);
  const [teamPreviewLoading, setTeamPreviewLoading] = useState(false);

  const load = useCallback(async (refresh = false) => {
    try {
      const o = await consoleApi<Overview>(`/overview${refresh ? '?refresh=1' : ''}`);
      setOverview(o);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // While the backend is warming its first scan, poll until data lands.
  useEffect(() => {
    if (overview?.status !== 'warming') return undefined;
    const t = setTimeout(() => load(), 5000);
    return () => clearTimeout(t);
  }, [overview, load]);

  useEffect(() => {
    try { localStorage.setItem(THRESHOLD_KEY, String(threshold)); } catch { /* private mode */ }
  }, [threshold]);

  const data = overview?.data || null;
  const labels = overview?.labels || {};
  const nameOf = (org: string) => labels[org] || org;
  const paceTarget = data?.target.pace_hours_per_day ?? 3;
  const today = data?.today ?? pacificToday();

  const devicesByOrg = useMemo(() => {
    const m: Record<string, E2Device[]> = {};
    (data?.devices || []).forEach((d) => { (m[d.org] = m[d.org] || []).push(d); });
    return m;
  }, [data]);

  const orgRows: OrgRow[] = useMemo(() => (data?.orgs || []).map((o, i) => {
    const devs = devicesByOrg[o.org] || [];
    const silentHeadsets = devs.filter((d) => {
      const s = deviceSilent(d, today);
      return !d.last_recording || (s != null && s >= threshold);
    }).length;
    return {
      ...o,
      rank: i + 1,
      status: statusOf(o.last_recording, o.silent_days, o.pace, paceTarget, threshold),
      silentHeadsets,
      neverHeadsets: devs.filter((d) => !d.last_recording).length,
    };
  }), [data, devicesByOrg, today, paceTarget, threshold]);

  const silentDevices = useMemo(() => (data?.devices || [])
    .map((d) => ({ ...d, silent: deviceSilent(d, today) }))
    .filter((d) => !d.last_recording || (d.silent != null && d.silent >= threshold))
    .sort((a, b) => {
      if (!a.last_recording !== !b.last_recording) return a.last_recording ? 1 : -1;
      return (b.silent ?? 0) - (a.silent ?? 0) || a.org.localeCompare(b.org) || a.id.localeCompare(b.id);
    }), [data, today, threshold]);

  const silentOrgs = orgRows.filter((o) => o.status === 'not_recording' || o.status === 'never');

  const visibleOrgs = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = orgRows.filter((o) => {
      if (q && !o.org.toLowerCase().includes(q) && !nameOf(o.org).toLowerCase().includes(q)) return false;
      if (notRecordingOnly && !(o.status === 'not_recording' || o.status === 'never' || o.silentHeadsets > 0)) {
        return false;
      }
      if (statusFilter !== 'all' && o.status !== statusFilter) return false;
      return true;
    });
    const val = (o: OrgRow): number | string => {
      switch (sortKey) {
        case 'org': return nameOf(o.org).toLowerCase();
        case 'pace': return o.pace;
        case 'headset_count': return o.headset_count;
        case 'silent_headsets': return o.silentHeadsets;
        // never recorded sorts as the most silent
        case 'silent': return o.silent_days == null ? Number.MAX_SAFE_INTEGER : o.silent_days;
        default: return o.week_hours;
      }
    };
    return [...rows].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number);
      return (sortDesc ? -cmp : cmp) || a.rank - b.rank;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgRows, search, notRecordingOnly, statusFilter, sortKey, sortDesc, labels]);

  const sortBy = (k: SortKey) => {
    if (k === sortKey) setSortDesc(!sortDesc);
    else { setSortKey(k); setSortDesc(k !== 'org'); }
  };
  const arrow = (k: SortKey) => (k === sortKey ? (sortDesc ? ' ▼' : ' ▲') : '');

  const saveSettings = async (patch: Record<string, unknown>) => {
    setSavingSettings(true);
    try {
      await consoleApi('/settings', { method: 'PUT', body: JSON.stringify(patch) });
      await load();
      setNotice('Email settings saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingSettings(false);
      setConfirmGlobalOn(false);
    }
  };

  const openTeamPreview = async () => {
    setTeamPreviewLoading(true);
    try {
      setTeamPreview(await consoleApi<EmailPreview>('/preview-internal'));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setTeamPreviewLoading(false);
    }
  };

  const emailStates = overview?.orgs || {};
  const wouldSendCount = Object.values(emailStates).filter((s) => s.enabled && s.recipients.length > 0).length;

  const columns: DataTableColumn<OrgRow>[] = [
    { key: 'rank', header: '#', className: 'cm-col-num', render: (o) => o.rank },
    {
      key: 'org',
      header: `Customer${arrow('org')}`,
      onHeaderClick: () => sortBy('org'),
      render: (o) => {
        const em = emailStates[o.org];
        return (
          <div className="cm-org-cell">
            <span className="cm-org-name">{nameOf(o.org)}</span>
            <span className="cm-muted cm-org-sub">
              {o.headset_count} headset{o.headset_count === 1 ? '' : 's'} · {o.active_week} recorded in 7 days
            </span>
            {em && (
              <span
                className={`cm-mail-dot ${em.will_send ? 'cm-mail-on' : 'cm-mail-off'}`}
                title={em.will_send ? 'Weekly email on' : `Weekly email off: ${em.reason || ''}`}
              >
                {em.will_send ? 'Email on' : 'Email off'}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'week_hours',
      header: `Last 7 days / target${arrow('week_hours')}`,
      onHeaderClick: () => sortBy('week_hours'),
      render: (o) => {
        const pct = o.week_target_hours ? Math.min(100, (100 * o.week_hours) / o.week_target_hours) : 0;
        return (
          <div className="cm-progress-cell">
            <span><strong>{fmtH(o.week_hours)}</strong> / {fmtH(o.week_target_hours, 0)} h</span>
            <div className="cm-bar"><div className={`cm-bar-fill cm-fill-${o.status}`} style={{ width: `${pct}%` }} /></div>
          </div>
        );
      },
    },
    {
      key: 'pace',
      header: `h/day/headset${arrow('pace')}`,
      className: 'cm-col-num',
      onHeaderClick: () => sortBy('pace'),
      render: (o) => (
        <span className={o.pace >= paceTarget ? 'cm-good' : 'cm-bad'}>
          {fmtH(o.pace, 2)} <span className="cm-muted">/ {paceTarget}</span>
        </span>
      ),
    },
    {
      key: 'top',
      header: 'Top headset',
      render: (o) => (o.top
        ? <span><code className="cm-id">{o.top.id}</code> <span className="cm-muted">{fmtH(o.top.week_hours)} h</span></span>
        : <span className="cm-muted">–</span>),
    },
    {
      key: 'total_hours',
      header: 'All-time',
      className: 'cm-col-num',
      render: (o) => (o.total_hours ? `${fmtH(o.total_hours)} h` : <span className="cm-muted">–</span>),
    },
    {
      key: 'daily',
      header: 'Daily',
      render: (o) => {
        const line = paceTarget * o.headset_count;
        return (
          <span className="cm-spark" title={`Daily hours, last 7 days; full height is ${line} h (${paceTarget} h × ${o.headset_count})`}>
            {o.days.map((d, j) => (
              <i key={j} className={d >= line ? 'cm-spark-top' : d === 0 ? 'cm-spark-zero' : ''}
                style={{ height: `${Math.max(8, Math.min(100, line ? (d / line) * 100 : 0))}%` }} />
            ))}
          </span>
        );
      },
    },
    {
      key: 'silent',
      header: `Last recording${arrow('silent')}`,
      onHeaderClick: () => sortBy('silent'),
      render: (o) => (
        <div className="cm-progress-cell">
          <span>{fmtStamp(o.last_recording)}</span>
          <span className={`cm-org-sub ${!o.last_recording || (o.silent_days ?? 0) >= threshold ? 'cm-bad' : 'cm-muted'}`}>
            {silentLabel(o.silent_days, o.last_recording)}
          </span>
        </div>
      ),
    },
    {
      key: 'silent_headsets',
      header: `Not recording${arrow('silent_headsets')}`,
      className: 'cm-col-num',
      onHeaderClick: () => sortBy('silent_headsets'),
      render: (o) => (o.silentHeadsets
        ? <span className="cm-bad">{o.silentHeadsets} of {o.headset_count}</span>
        : <span className="cm-muted">0</span>),
    },
  ];

  if (loading) return <LoadingSpinner message="Loading customer headsets…" />;

  const settings = overview?.settings;
  const schedule = overview?.schedule;
  const h = data?.hours;
  const fleetPace = h?.fleet_pace ?? 0;
  const silentHeadsetCount = silentDevices.length;

  return (
    <div className="cm-page">
      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>
      <Alert type="success" onDismiss={() => setNotice(null)}>{notice}</Alert>

      {overview?.status === 'warming' && (
        <LoadingSpinner message="Scanning E2 headsets for the first time… this page updates by itself." />
      )}

      {data && (
        <>
          <div className="cm-toolbar">
            <span className="cm-muted">
              Data as of {fmtWhen(data.generatedAt)} · target {paceTarget} h per headset per day
              ({data.target.week_hours_per_headset} h a week)
            </span>
            <span className="cm-toolbar-actions">
              <Button variant="outline" size="sm" onClick={() => navigate('/e2-summit')}>
                Open E2 Summit wall
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setNotice('Refresh started; give it a minute.'); load(true); }}>
                Refresh data
              </Button>
            </span>
          </div>

          {/* Summary strip */}
          <div className="cm-summary">
            <div className="cm-stat">
              <div className="cm-stat-label">E2 headsets in the field</div>
              <div className="cm-stat-value">{data.headsets.customer}</div>
              <div className="cm-stat-sub">
                {data.headsets.active_week} of {data.headsets.customer} recorded in the last 7 days
                · {data.headsets.orgs} customer{data.headsets.orgs === 1 ? '' : 's'}
              </div>
            </div>
            <div className="cm-stat">
              <div className="cm-stat-label">Hours of E2 footage</div>
              <div className="cm-stat-value">{fmtH(h?.total)}</div>
              <div className="cm-stat-sub">
                {h?.next_camp
                  ? <>next camp {h.next_camp.toLocaleString()} h · {fmtH(h.to_next_camp)} to go</>
                  : 'all time'}
              </div>
            </div>
            <div className="cm-stat">
              <div className="cm-stat-label">Fleet h/day/headset</div>
              <div className={`cm-stat-value ${fleetPace >= paceTarget ? 'cm-good' : 'cm-bad'}`}>
                {fmtH(fleetPace, 2)} <span className="cm-stat-unit">/ {paceTarget}</span>
              </div>
              <div className="cm-stat-sub">{data.headsets.at_pace_week} headsets at target this week</div>
            </div>
            <div className="cm-stat">
              <div className="cm-stat-label">Hours, last 7 days</div>
              <div className="cm-stat-value">{fmtH(h?.week)}</div>
              <div className="cm-stat-sub">
                {h?.at_pace_week_hours ? <>of {fmtH(h.at_pace_week_hours, 0)} h at target · </> : null}
                {fmtH(h?.prev_week)} the 7 days before
              </div>
            </div>
            <div className={`cm-stat ${silentHeadsetCount ? 'cm-stat-alert' : ''}`}>
              <div className="cm-stat-label">Not recording ({threshold}+ days)</div>
              <div className="cm-stat-value">
                {silentOrgs.length} <span className="cm-stat-unit">customers</span>{' '}
                · {silentHeadsetCount} <span className="cm-stat-unit">headsets</span>
              </div>
              <div className="cm-stat-sub">includes {silentDevices.filter((d) => !d.last_recording).length} never recorded</div>
            </div>
          </div>

          {/* Org leaderboard */}
          <section className="cm-card">
            <div className="cm-section-head">
              <h2>Leaderboard · last 7 days</h2>
              <div className="cm-filters">
                <input
                  type="search"
                  placeholder="Search customers"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'all' | Status)}>
                  <option value="all">All statuses</option>
                  {(Object.keys(STATUS_LABEL) as Status[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
                <select value={`${sortKey}:${sortDesc ? 'desc' : 'asc'}`} onChange={(e) => {
                  const [k, dir] = e.target.value.split(':');
                  setSortKey(k as SortKey);
                  setSortDesc(dir === 'desc');
                }}>
                  <option value="week_hours:desc">Most hours this week</option>
                  <option value="week_hours:asc">Fewest hours this week</option>
                  <option value="pace:asc">Furthest behind target</option>
                  <option value="silent:desc">Longest silent</option>
                  <option value="silent_headsets:desc">Most silent headsets</option>
                  <option value="headset_count:desc">Most headsets</option>
                  <option value="org:asc">Name A–Z</option>
                </select>
                <label className="cm-check">
                  <input type="checkbox" checked={notRecordingOnly} onChange={(e) => setNotRecordingOnly(e.target.checked)} />
                  Not recording only
                </label>
              </div>
            </div>
            <DataTable
              className="cm-table"
              columns={columns}
              data={visibleOrgs}
              rowKey={(o) => o.org}
              onRowClick={(o) => setSelectedOrg(o.org)}
              rowClassName={(o) => (o.status === 'not_recording' || o.status === 'never' ? 'cm-row-silent' : undefined)}
              emptyMessage="No customers match these filters."
            />
            <p className="cm-muted cm-footnote">
              Same leaderboard as the E2 Summit wall. The last 7 days include today. Click a customer to see every headset, its daily hours and its email settings.
            </p>
          </section>

          {/* Who isn't recording */}
          <section className="cm-card">
            <div className="cm-section-head">
              <h2>Who isn’t recording</h2>
              <label className="cm-inline-field">
                No recording in the last
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={threshold}
                  onChange={(e) => setThreshold(Math.min(60, Math.max(1, Number(e.target.value) || 1)))}
                  className="cm-threshold"
                />
                days
              </label>
            </div>
            {silentDevices.length === 0 ? (
              <div className="empty-state">Every customer headset recorded in the last {threshold} days.</div>
            ) : (
              <div className="cm-silent-grid">
                <div>
                  <h3>Customers ({silentOrgs.length})</h3>
                  {silentOrgs.length === 0 ? (
                    <p className="cm-muted">Every customer has at least one headset recording.</p>
                  ) : (
                    <ul className="cm-silent-list">
                      {silentOrgs
                        .sort((a, b) => (b.silent_days ?? 1e9) - (a.silent_days ?? 1e9))
                        .map((o) => (
                          <li key={o.org}>
                            <button className="cm-link" onClick={() => setSelectedOrg(o.org)}>{nameOf(o.org)}</button>
                            <span className={`cm-badge cm-badge-${o.status}`}>
                              {o.status === 'never' ? 'Never recorded' : `${o.silent_days} days silent`}
                            </span>
                            <span className="cm-muted">{o.headset_count} headset{o.headset_count === 1 ? '' : 's'}</span>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
                <div>
                  <h3>Headsets ({silentDevices.length})</h3>
                  <ul className="cm-silent-list">
                    {silentDevices.map((d) => (
                      <li key={d.id}>
                        <code className="cm-id">{d.id}</code>
                        <button className="cm-link" onClick={() => setSelectedOrg(d.org)}>{nameOf(d.org)}</button>
                        <span className={`cm-badge cm-badge-${d.last_recording ? 'not_recording' : 'never'}`}>
                          {d.last_recording ? `${d.silent} days silent` : 'Never recorded'}
                        </span>
                        <span className="cm-muted">
                          {d.last_recording ? `last ${fmtStamp(d.last_recording)} · ${fmtH(d.total_hours)} h total` : 'no footage yet'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </section>
        </>
      )}

      {/* Weekly email controls */}
      {settings && schedule && (
        <section className="cm-card cm-email-bar">
          <div className="cm-team-row">
            <label className="cm-switch">
              <input
                type="checkbox"
                checked={settings.internal_enabled}
                disabled={savingSettings}
                onChange={(e) => saveSettings({ internal_enabled: e.target.checked })}
              />
              <span className="cm-switch-track" aria-hidden="true" />
              <span className="cm-switch-label">
                Team weekly email {settings.internal_enabled ? 'ON' : 'OFF'}
              </span>
            </label>
            <span className="cm-muted cm-team-what">
              A summary of every customer, plus previews of the summary and reminder each customer would get.
            </span>
            <label className="cm-inline-field cm-team-to">
              To
              <input
                type="text"
                value={teamText ?? settings.internal_recipients.join(', ')}
                onChange={(e) => setTeamText(e.target.value)}
                aria-label="Team email recipients"
              />
            </label>
            {teamText !== null && teamText !== settings.internal_recipients.join(', ') && (
              <Button size="sm" variant="primary" loading={savingSettings}
                onClick={async () => { await saveSettings({ internal_recipients: teamText }); setTeamText(null); }}>
                Save recipients
              </Button>
            )}
            <Button size="sm" variant="outline" loading={teamPreviewLoading} onClick={openTeamPreview}>
              Preview team summary
            </Button>
            <span className="cm-muted">
              Last team send: {settings.internal_last_sent
                ? `${settings.internal_last_sent.status} ${fmtWhen(settings.internal_last_sent.at)}`
                : 'not yet'}
            </span>
          </div>
          <div className="cm-email-main">
            <label className="cm-switch">
              <input
                type="checkbox"
                checked={settings.global_enabled}
                disabled={savingSettings}
                onChange={(e) => {
                  if (e.target.checked) setConfirmGlobalOn(true);
                  else saveSettings({ global_enabled: false });
                }}
              />
              <span className="cm-switch-track" aria-hidden="true" />
              <span className="cm-switch-label">
                Customer emails {settings.global_enabled ? 'ON' : 'OFF'} for everyone
              </span>
            </label>
            <label className="cm-inline-field">
              Customers on “inherit”
              <select
                value={settings.default_on ? 'on' : 'off'}
                disabled={savingSettings}
                onChange={(e) => saveSettings({ default_on: e.target.value === 'on' })}
              >
                <option value="on">get the email</option>
                <option value="off">don’t get the email</option>
              </select>
            </label>
            <label className="cm-inline-field">
              Send
              <select
                value={settings.send_weekday}
                disabled={savingSettings}
                onChange={(e) => saveSettings({ send_weekday: Number(e.target.value) })}
              >
                {(overview?.weekdays || []).map((d, i) => <option key={d} value={i}>{d}s</option>)}
              </select>
              at
              <input
                type="time"
                defaultValue={settings.send_time}
                key={settings.send_time}
                disabled={savingSettings}
                onBlur={(e) => { if (e.target.value && e.target.value !== settings.send_time) saveSettings({ send_time: e.target.value }); }}
              />
              <span className="cm-muted">{settings.timezone}</span>
            </label>
          </div>
          <div className="cm-email-meta">
            <span>
              Next send: <strong>{schedule.next_send_label}</strong> (reports the week of {fmtStamp(schedule.next_report_week)})
              {!settings.global_enabled && <span className="cm-muted"> · no customer is emailed while customer emails are off</span>}
            </span>
            <span>{wouldSendCount} customers resolve to on with recipients</span>
            {overview?.sandbox && (
              <span className="cm-sandbox">Sandbox: sends are redirected to internal test recipients</span>
            )}
          </div>
          {confirmGlobalOn && (
            <Alert type="warning">
              <div className="cm-confirm">
                <span>
                  Turn customer emails on? {wouldSendCount} customer{wouldSendCount === 1 ? '' : 's'} with
                  recipients will be emailed {schedule.send_label}, starting {schedule.next_send_label}.
                </span>
                <Button size="sm" variant="primary" loading={savingSettings} onClick={() => saveSettings({ global_enabled: true })}>
                  Turn on
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmGlobalOn(false)}>Cancel</Button>
              </div>
            </Alert>
          )}
        </section>
      )}


      {selectedOrg && data && overview && (
        <CustomerOrgDetail
          org={selectedOrg}
          label={nameOf(selectedOrg)}
          snapshot={data}
          orgRow={orgRows.find((o) => o.org === selectedOrg) || null}
          devices={devicesByOrg[selectedOrg] || []}
          email={emailStates[selectedOrg] || null}
          globalEnabled={!!settings?.global_enabled}
          defaultOn={!!settings?.default_on}
          nextReportWeek={schedule?.next_report_week || ''}
          threshold={threshold}
          onClose={() => setSelectedOrg(null)}
          onSaved={(msg) => { setNotice(msg); load(); }}
          onError={(msg) => setError(msg)}
        />
      )}
      {teamPreview && (
        <div className="cm-modal" onClick={() => setTeamPreview(null)}>
          <div className="cm-modal-body" onClick={(e) => e.stopPropagation()}>
            <header className="cm-drawer-head">
              <div>
                <div className="cm-muted">Subject · to {(teamPreview.recipients || []).join(', ') || 'nobody yet'}</div>
                <strong>{teamPreview.subject}</strong>
                {teamPreview.partial && (
                  <div className="cm-muted">This week is not over yet; numbers will grow until it ends.</div>
                )}
              </div>
              <button className="cm-close" onClick={() => setTeamPreview(null)} aria-label="Close preview">&times;</button>
            </header>
            <iframe title="Team summary preview" className="cm-preview-frame" srcDoc={teamPreview.html} sandbox="" />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagementPage;
